import { ReactElement, useMemo } from 'react'
import { useController } from 'react-hook-form'
import { reject, sortBy, toNumber } from 'lodash-es'
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { SelectLabeled } from '@ui/actions'
import { roofStoreyKey, useModelStore } from '@editorStores'
import { PLAN_STOREY_POLICY_OPTIONS, PlanSettingsFormValues } from './utils'

const PlanSettingsFields = (): ReactElement => {
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const { field: planStoreyPolicyField } = useController<
    PlanSettingsFormValues,
    'planStoreyPolicy'
  >({
    name: 'planStoreyPolicy',
  })
  const { field: storeysField } = useController<PlanSettingsFormValues, 'storeysForPlans'>({
    name: 'storeysForPlans',
  })

  const sortedStoreys = useMemo(() => {
    const nonRoof = reject(Array.from(availableStoreys), storey => storey === roofStoreyKey)
    return [...sortBy(nonRoof, storey => toNumber(storey)), roofStoreyKey]
  }, [availableStoreys])

  const storeysForPlans = storeysField.value ?? []
  const toggleStorey = (storey: string) =>
    storeysField.onChange(
      storeysForPlans.includes(storey)
        ? storeysForPlans.filter(selected => selected !== storey)
        : [...storeysForPlans, storey],
    )

  return (
    <Stack spacing={1}>
      <SelectLabeled
        label="Stockwerk für Planerstellung"
        value={planStoreyPolicyField.value}
        onChange={planStoreyPolicyField.onChange}
        options={PLAN_STOREY_POLICY_OPTIONS}
        data-cy="plan-settings-policy"
      />

      {planStoreyPolicyField.value === 'UserDefined' && (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
              flex: 1,
              minWidth: 0,
            }}
          >
            {sortedStoreys.map(storey => (
              <FormControlLabel
                key={storey}
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={storeysForPlans.includes(storey)}
                    onChange={() => toggleStorey(storey)}
                    data-cy={`plan-settings-storey-${storey}`}
                  />
                }
                label={<Typography variant="body2">{storeyToLabel(storey)}</Typography>}
              />
            ))}
          </Box>
        </Stack>
      )}
    </Stack>
  )
}

export default PlanSettingsFields
